import React, { useCallback, useEffect, useState } from 'react';
import logger from '../../utils/logger';

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow' }) : '');
const fmtSize = (bytes) => (bytes ? `${(bytes / 1048576).toFixed(1)} МБ` : '');

/**
 * «Недавно удалённые кабинеты»: восстановить из корзины (30 дней) и скачать копию данных.
 * Используется и в Настройках (admin=false — только свои), и в админке (admin=true — все, плюс восстановление
 * физически стёртых кабинетов из архива).
 */
function DeletedCabinets({ request, fetchBlob, showStatus, onChanged, admin = false }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [resume, setResume] = useState({});

  const load = useCallback(async () => {
    try {
      setItems(await request(admin ? '/api/v1/cabinet-trash/admin/all' : '/api/v1/cabinet-trash', {}));
    } catch (error) {
      logger.error(error);
      setItems([]);
    }
  }, [request, admin]);

  useEffect(() => { load(); }, [load]);

  const restore = async (item) => {
    setBusyId(item.account_id);
    try {
      const url = item.purged
        ? `/api/v1/cabinet-trash/admin/archives/${item.archive_id}/restore`
        : `/api/v1/cabinet-trash/${item.account_id}/restore${resume[item.account_id] ? '?resume_automations=true' : ''}`;
      const result = await request(url, { method: 'POST' });
      showStatus && showStatus('success', result?.note ? `Кабинет «${item.name}» восстановлен. ${result.note}` : `Кабинет «${item.name}» восстановлен.`);
      await load();
      if (onChanged) await onChanged();
    } catch (error) {
      logger.error(error);
      showStatus && showStatus('error', error?.message || 'Не удалось восстановить кабинет.');
    } finally {
      setBusyId('');
    }
  };

  const download = async (item) => {
    setBusyId(item.account_id);
    try {
      const blob = await fetchBlob(`/api/v1/cabinet-trash/archives/${item.archive_id}/download`);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${item.name}_архив.sql.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (error) {
      logger.error(error);
      showStatus && showStatus('error', error?.message || 'Не удалось скачать архив.');
    } finally {
      setBusyId('');
    }
  };

  if (items === null) return null;
  if (items.length === 0 && !admin) return null;

  return (
    <div style={{ borderTop: '2px solid var(--surface-border)', paddingTop: '24px', marginTop: '24px' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--ink)' }}>
        {admin ? 'Удалённые кабинеты и архивы' : 'Недавно удалённые кабинеты'}
      </h3>
      <p className="muted" style={{ margin: '0 0 12px' }}>
        Удалённый кабинет 30 дней хранится целиком — его можно восстановить. Потом данные уходят в закрытый архив
        {admin ? ' (восстановление из архива — здесь, у администратора).' : ', вернуть их можно через поддержку. Копию данных можно скачать, пока действует ссылка из письма.'}
      </p>
      <div className="panel">
        {items.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center' }} className="muted">Удалённых кабинетов нет.</div>
        ) : items.map((item) => (
          <div
            key={`${item.account_id}-${item.archive_id || ''}`}
            style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.4fr) minmax(160px, 2fr) auto', gap: 12, alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--surface-border)' }}
          >
            <div>
              <strong>{item.name}</strong>
              <div className="muted" style={{ fontSize: 13 }}>
                {item.marketplace_type === 'wildberries' ? 'Wildberries' : 'OZON'} · удалён {fmtDate(item.deleted_at)}{admin && item.owner_email ? ` · ${item.owner_email}` : ''}
              </div>
            </div>
            <div style={{ fontSize: 14 }}>
              {item.in_trash
                ? <>В корзине до <strong>{fmtDate(item.restore_until)}</strong>, потом данные уходят в архив.</>
                : <>Данные в закрытом архиве{item.archive_size ? ` (${fmtSize(item.archive_size)})` : ''}.</>}
              {item.archive_error && <div style={{ color: 'var(--text-danger)' }}>Архив не создан: {item.archive_error}</div>}
              {item.emailed_at && <div className="muted" style={{ fontSize: 13 }}>Письмо со ссылкой отправлено {fmtDate(item.emailed_at)}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {item.archive_ready && fetchBlob && (
                <button type="button" className="ghost" disabled={busyId === item.account_id} onClick={() => download(item)}>Скачать архив</button>
              )}
              {item.in_trash && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(resume[item.account_id])}
                      onChange={(event) => setResume((prev) => ({ ...prev, [item.account_id]: event.target.checked }))}
                    />
                    Включить автоматизации обратно
                  </label>
                  <button type="button" className="primary" disabled={busyId === item.account_id} onClick={() => restore(item)}>Восстановить</button>
                </>
              )}
              {admin && item.purged && item.archive_id && (
                <button type="button" className="primary" disabled={busyId === item.account_id} onClick={() => restore(item)}>Восстановить из архива</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DeletedCabinets;
